import Model, { attr, belongsTo, hasMany } from '@ember-data/model';
import type EmployeeModel from './employee.ts';
import type AttachmentModel from './attachment.ts';
import type PslfFormModel from './pslf-form.ts';
import type PslfAssetModel from './pslf-asset.ts';
import type PslfGroup from './pslf-group.ts';
import { Type } from '@warp-drive/core-types/symbols';

export default class PslfProfileModel extends Model {
  @attr declare isInterested: boolean;
  @attr declare ssn: string;
  @attr declare signature: string;
  @attr declare signaturePresent: boolean;

  /*************************
   **  Relationships      **
   *************************/

  @belongsTo('employee', { async: false, inverse: 'pslfProfile' })
  declare employee: EmployeeModel;
  @hasMany('attachment', { async: false, inverse: null })
  declare attachments: AttachmentModel[];
  @hasMany('pslf-form', { async: false, inverse: 'pslfProfile' })
  declare pslfForms: PslfFormModel[];
  @hasMany('pslf-asset', { async: false, inverse: 'pslfProfiles' })
  declare pslfAssets: PslfAssetModel[];

  /**************************
   **  Computed Properties **
   **************************/

  normalizeFein(fein?: string) {
    return fein?.replace(/\D/g, '');
  }

  get fein() {
    const employeeFein =
      (this.employee.employeeAddition?.processed?.merged?.fein as string) ||
      this.employee.customAttributes?.fein;
    if (!employeeFein) {
      return;
    }
    return this.normalizeFein(employeeFein);
  }

  get partnerGroups() {
    return this.employee.company.pslfGroups;
  }

  get employerGroup() {
    if (!this.fein) {
      return;
    }
    return this.partnerGroups?.find(
      (group: PslfGroup) => this.normalizeFein(group.employerFein) === this.fein
    );
  }

  get feinIsRogue() {
    if (!this.fein || this.employerGroup) {
      return false;
    }
    return true;
  }

  [Type] = 'pslf-profile' as const;
}
